import React from 'react';
import { matchPath, useLocation, useRouteMatch } from 'react-router';

import { WrapMaybe } from 'src/components/wrapMaybe/WrapMaybe';
import { IsCloudEndpointInitiatingContextProvider } from 'src/hooks/useIsCloudEndpointInitiating';
import { useLocalStorage } from 'src/hooks/useLocalStorage';
import { usePerKeyLocalStorage } from 'src/hooks/usePerKeyLocalStorage';
import { favicons } from 'src/templates/index/favicons';
import { getFavicon } from 'src/templates/index/getFavicon';

import { HeadersManagerContextProvider } from '../explorerPage/hooks/useExplorerState/useHeadersManagerContext/useHeadersManagerContext';
import {
  ReachabilityProvider,
  useEndpointReachability,
} from '../hooks/useEndpointReachability';
import { GraphRef } from '../hooks/useGraphRef';
import {
  IntrospectionSchemaProvider,
  RegisteredSchemaProvider,
} from '../hooks/useSchema';
import {
  changelogRouteConfig,
  explorerRouteConfig,
  graphSettingsRouteConfig,
  proposalHomepageRouteConfig,
  proposalSchemaRouteConfig,
  sandboxGraphRouteConfig,
  schemaRouteConfig,
  variantHomepageRouteConfig,
  variantSettingsRouteConfig,
} from '../routes';

import { GraphBehavioralFlags } from './graphBehavioralFlags/graphBehavioralFlags';

const getConnectivityStatusFavicon = ({
  endpointReachable,
}: {
  endpointReachable: boolean;
}) => {
  const darkMode = window.matchMedia('(prefers-color-scheme: dark)').matches;
  if (endpointReachable) {
    return darkMode ? favicons.connected.dark : favicons.connected.light;
  }

  return darkMode ? favicons.disconnected.dark : favicons.disconnected.light;
};

const FaviconUpdater = ({
  shouldAutoUpdateIntrospectionSchema,
}: {
  shouldAutoUpdateIntrospectionSchema?: boolean;
}) => {
  const endpointReachability = useEndpointReachability();
  React.useEffect(() => {
    const favicon = document.getElementById('favicon') as HTMLLinkElement;
    if (favicon) {
      if (!shouldAutoUpdateIntrospectionSchema) {
        favicon.href = getFavicon({
          hostname: window.location.hostname,
          darkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
          favicons,
        });
      } else {
        favicon.href = getConnectivityStatusFavicon({
          endpointReachable: endpointReachability === 'reachable',
        });
      }
    }
    return () => {
      if (favicon) {
        favicon.href = getFavicon({
          hostname: window.location.hostname,
          darkMode: window.matchMedia('(prefers-color-scheme: dark)').matches,
          favicons,
        });
      }
    };
  }, [endpointReachability, shouldAutoUpdateIntrospectionSchema]);

  return null;
};

export const VariantLayoutProviders = ({
  graphRef,
  children,
  graphBehavioralFlags,
}: {
  graphRef: GraphRef | null;
  children: React.ReactNode;
  graphBehavioralFlags: GraphBehavioralFlags;
}) => {
  const location = useLocation();
  const isSandboxRoute = !!useRouteMatch(sandboxGraphRouteConfig.definition);

  const isSchemaPoweredPage = [
    schemaRouteConfig,
    sandboxGraphRouteConfig,
    explorerRouteConfig,
    graphSettingsRouteConfig,
    variantSettingsRouteConfig,
    variantHomepageRouteConfig,
    changelogRouteConfig,
    proposalHomepageRouteConfig,
    proposalSchemaRouteConfig,
  ].some((config) =>
    matchPath(location.pathname, { path: config.definition, exact: false }),
  );

  const [sandboxUrl] = useLocalStorage('sandboxUrl');
  const [sandboxConnectionSettings] = usePerKeyLocalStorage({
    localStorageKey: 'sandboxConnectionSettings',
    key: sandboxUrl,
    initialValue:
      graphBehavioralFlags.sandboxConnectionSettingsInitialValue(sandboxUrl),
  });

  const SchemaProvider = isSandboxRoute ? (
    <IntrospectionSchemaProvider
      sandboxConnectionSettings={sandboxConnectionSettings}
    />
  ) : graphRef ? (
    // If we are not on a sandbox route, we should have graphRef
    <RegisteredSchemaProvider graphRef={graphRef} />
  ) : (
    <div />
  );

  return (
    <HeadersManagerContextProvider graphRef={graphRef}>
      <IsCloudEndpointInitiatingContextProvider graphRef={graphRef}>
        <ReachabilityProvider>
          <WrapMaybe on={isSchemaPoweredPage} element={SchemaProvider}>
            <>
              {isSchemaPoweredPage && isSandboxRoute && (
                <FaviconUpdater
                  shouldAutoUpdateIntrospectionSchema={
                    sandboxConnectionSettings.shouldAutoUpdateSchema
                  }
                />
              )}
              {children}
            </>
          </WrapMaybe>
        </ReachabilityProvider>
      </IsCloudEndpointInitiatingContextProvider>{' '}
    </HeadersManagerContextProvider>
  );
};
